import React, { useEffect, useCallback, useRef } from 'react';
import { Icon, Typography } from '@growthaxl/web-foundation';
import { useAuth } from '../../Auth';
import { useShellStore } from '../../../shell/store/shellStore';
import { shellEventBus } from '../../../shell/events/eventBus';
import { USER_THEME_STORAGE_KEY } from '../../../constants/storageKeys';

export interface UserMenuProps {
  open: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
}

export function UserMenu({ open, onClose, anchorRef }: UserMenuProps): React.ReactElement {
  const { user, logout } = useAuth();
  const storeTheme = useShellStore(state => state.personalization?.theme);
  const theme = storeTheme ?? (typeof window !== 'undefined' && localStorage.getItem(USER_THEME_STORAGE_KEY) === 'dark' ? 'dark' : 'light');
  const setUserTheme = useShellStore(state => state.setUserTheme);
  const menuRef = useRef<HTMLDivElement>(null);
  const wasOpenRef = useRef(false);
  const isDark = theme === 'dark';

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, handleEscape]);

  useEffect(() => {
    if (!open) return;

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || anchorRef.current?.contains(target)) {
        return;
      }
      onClose();
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open, onClose, anchorRef]);

  useEffect(() => {
    if (wasOpenRef.current && !open) {
      anchorRef.current?.focus();
    }
    wasOpenRef.current = open;
  }, [open, anchorRef]);

  if (!open) return <></>;

  const handleThemeToggle = () => {
    const newTheme = isDark ? 'light' : 'dark';
    setUserTheme(newTheme);
    shellEventBus.dispatch({ type: 'THEME_CHANGED', payload: { mode: newTheme } });
  };

  const handleLogout = () => {
    logout();
  };

  const menuItemClass =
    'flex w-full items-center gap-3 border-0 bg-transparent px-5 py-2.5 text-left font-inherit cursor-pointer transition-colors duration-fast hover:bg-surface-hover';
  const menuItemTextClass = 'text-label leading-normal font-normal';

  return (
    <div
      ref={menuRef}
      data-testid="user-menu-dropdown"
      role="menu"
      className="absolute right-0 top-[42px] z-[200] w-[260px] overflow-hidden rounded-12 border border-border-default bg-surface-elevated text-text-primary shadow-lg"
    >
      {/* Profile Section */}
      <div className="border-b border-border-default px-5 py-4">
        <Typography
          variant="heading"
          color="primary"
          component="div"
          data-testid="user-menu-name"
          className="text-body-md font-semibold leading-tight"
        >
          {user?.name ?? 'Guest'}
        </Typography>
        <Typography
          variant="body2"
          color="secondary"
          component="div"
          data-testid="user-menu-email"
          className={`mt-1 ${menuItemTextClass}`}
        >
          {user?.email ?? ''}
        </Typography>
        {user?.roles?.[0] ? (
          <Typography
            variant="caption"
            color="primaryColor"
            component="div"
            data-testid="user-menu-role"
            className="mt-0.5 text-caption leading-normal"
          >
            {user.roles[0]}
          </Typography>
        ) : null}
      </div>

      {/* Menu Items */}
      <div className="border-b border-border-default py-1.5">
        <button
          data-testid="user-menu-theme-toggle"
          onClick={handleThemeToggle}
          role="switch"
          aria-checked={isDark}
          className={`${menuItemClass} justify-between`}
          aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
        >
          <div className="flex min-w-0 items-center gap-3">
            <Icon name="theme" className="!size-6 shrink-0 text-text-secondary" />
            <Typography variant="body2" color="primary" component="span" className={menuItemTextClass}>
              {isDark ? 'Dark Mode' : 'Light Mode'}
            </Typography>
          </div>

          <div
            className={`relative h-6 w-9 shrink-0 rounded-full transition-colors duration-300 ${
              isDark ? 'bg-brand-secondary' : 'bg-border-strong'
            }`}
          >
            <div
              className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-surface shadow-sm transition-transform duration-300 ${
                isDark ? 'translate-x-3' : 'translate-x-0'
              }`}
            />
          </div>
        </button>
      </div>

      {/* Logout Section */}
      <div className="py-1.5">
        <button
          data-testid="user-menu-logout"
          type="button"
          role="menuitem"
          onClick={handleLogout}
          className={menuItemClass}
        >
          <Icon name="logout" className="!size-6 shrink-0 text-error" />
          <Typography variant="body2" color="primaryColor" component="span" className={`${menuItemTextClass} text-error`}>
            Log out
          </Typography>
        </button>
      </div>
    </div>
  );
}
